import {getAuth, type User} from 'firebase/auth';
import {getUserRoleRef} from '../../firebase/models/User';
import {useState} from 'react';
import {onSnapshot} from 'firebase/firestore';
import {Role} from 'types/role';
import {useEffect} from 'react';
import type {CourseId, UserId} from 'types/common';
import {firebaseStringToRole} from 'contexts/profileUtil';

// returns null if the user is not logged in
export function useUserId(): UserId | null {
  try {
    const auth = getAuth();

    // be really careful!
    return auth?.currentUser?.uid ?? null;
  } catch (e) {
    console.trace('Error in useUserId', e);
    return null;
  }
}

export function useUser(): User | null {
  try {
    const auth = getAuth();
    return auth?.currentUser ?? null;
  } catch (e) {
    console.trace('Error in useUser', e);
    return null;
  }
}

interface UseUserRoleParams {
  userId: UserId;
  courseId: CourseId;
}

interface UseUserRoleResult {
  userRole: Role;
  isLoading: boolean;
  error: Error | null;
}
export function useUserRole({
  userId,
  courseId,
}: UseUserRoleParams): UseUserRoleResult {
  const [userRole, setUserRole] = useState<Role>(Role.UNREGISTERED);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  useEffect(() => {
    if (!userId || !courseId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    const userRoleRef = getUserRoleRef(userId, courseId);
    const unsubscribe = onSnapshot(
      userRoleRef,
      doc => {
        setUserRole(firebaseStringToRole(doc.data()));
        setIsLoading(false);
      },
      error => {
        setError(error);
        setIsLoading(false);
      },
    );
    return () => unsubscribe();
  }, [userId, courseId]);
  return {userRole, isLoading, error};
}
